// §11.1 — Peek: `Space` on a selection shows it, `Space` or `Esc` puts it away.
//
// **It never launches the owning app.** That is the whole point of the feature and the thing its
// e2e asserts: looking at a file must not cost a window. The overlay lives inside Explorer's own
// window — §19.4's shell portal is for dialogs, pickers and permission prompts, which this is not.
import { useEffect, useState } from 'react';
import { extname, type FileEntry, type HangarFsApi } from '@hangar/shared';
import { COPY } from '@/copy';
import {
  PEEK_MAX_IMAGE_BYTES,
  PEEK_MAX_PDF_BYTES,
  PEEK_MAX_TEXT_BYTES,
  PEEK_PDF_ZOOM,
  WB_ARCHIVE_PEEK_ENTRIES,
} from '@/config';
// §11.8 — "used by Peek": the *same* renderer the reader uses, reached through `assets/**`
// because §6.5 forbids one app importing another and this is the seam it names instead (D-170).
import { PdfPageView } from '@/assets/pdf/PdfPageView';
import { parsePdf, type PdfPage } from '@/assets/pdf/document';
import { formatSize } from './model';
import { imageMime, peekKind } from './peek-kinds';
// §34.6 (M60.4) — the MIME for the blob, from the table Media and Peek share through `assets/**`
// (§6.5 forbids Explorer importing Media; D-170/D-508 is the route).
import { formatTime, mimeOfExtension } from '@/assets/media/kinds';

type Content =
  | { state: 'loading' }
  | { state: 'text'; text: string }
  | { state: 'image'; url: string }
  | { state: 'pdf'; page: PdfPage; pages: number }
  /** §34.2 — a zip's own entry list, capped, with the truncation stated rather than hidden. */
  | { state: 'archive'; names: string[]; more: number }
  /**
   * §34.6 — audio or video, as **metadata only**. `url` feeds an element with
   * `preload="metadata"`, which fetches the header and stops; `duration` arrives from it.
   */
  | { state: 'media'; kind: 'audio' | 'video'; url: string; duration: number }
  | { state: 'notice'; message: string };

export function Peek({
  fs,
  entry,
  onClose,
}: {
  fs: HangarFsApi;
  entry: FileEntry;
  onClose: () => void;
}) {
  const [content, setContent] = useState<Content>({ state: 'loading' });

  useEffect(() => {
    const kind = peekKind(entry.path);
    let cancelled = false;
    let url: string | null = null;

    const notice = (message: string): void => {
      if (!cancelled) setContent({ state: 'notice', message });
    };

    if (kind === 'none') {
      notice(COPY.explorer.peekNoPreview);
    } else if (kind === 'pdf') {
      // §11.1 — page one, and **never** a window: Peek renders it here, in Explorer's own overlay.
      if (entry.size > PEEK_MAX_PDF_BYTES) notice(COPY.explorer.peekTooLarge(entry.size));
      else {
        void fs
          .read(entry.path)
          .then((bytes) => {
            if (cancelled) return;
            const doc = parsePdf(bytes);
            const first = doc.pages[0];
            if (!first) notice(COPY.pdf.unreadable);
            else setContent({ state: 'pdf', page: first, pages: doc.pageCount });
          })
          .catch(() => notice(COPY.pdf.unreadable));
      }
    } else if (kind === 'archive') {
      // §34.2 (M56.4). The format module is imported **dynamically**: Explorer is on the boot
      // path, and a decompressor nobody has previewed a zip with should not be (D-507/D-508).
      void fs
        .read(entry.path)
        .then(async (bytes) => {
          const { listEntries } = await import('@/assets/archive/format');
          if (cancelled) return;
          const entries = listEntries(bytes);
          const names = entries.map((member) => member.path);
          setContent({
            state: 'archive',
            names: names.slice(0, WB_ARCHIVE_PEEK_ENTRIES),
            more: Math.max(0, names.length - WB_ARCHIVE_PEEK_ENTRIES),
          });
        })
        .catch(() => notice(COPY.archive.malformed));
    } else if (kind === 'audio' || kind === 'video') {
      // §34.6 — "poster (first frame via `preload=\"metadata\"`, never playing) + duration + codec
      // line". A blob URL for the reason the image branch gives: the bytes never become a string.
      //
      // **Nothing calls `play()` here, and the elements carry no `autoPlay` and no `controls`.**
      // Peek's whole contract is that looking at a file costs nothing (§11.1) — a preview that
      // started a two-hour film would be the loudest possible violation of it.
      void fs
        .read(entry.path)
        .then((bytes) => {
          if (cancelled) return;
          url = URL.createObjectURL(
            new Blob([bytes as BlobPart], { type: mimeOfExtension(extname(entry.path)) }),
          );
          setContent({ state: 'media', kind, url, duration: Number.NaN });
        })
        .catch((cause: unknown) => notice(cause instanceof Error ? cause.message : String(cause)));
    } else if (kind === 'text') {
      if (entry.size > PEEK_MAX_TEXT_BYTES) notice(COPY.explorer.peekTooLarge(entry.size));
      else {
        void fs
          .readText(entry.path)
          .then((text) => {
            if (!cancelled) setContent({ state: 'text', text });
          })
          .catch((cause: unknown) => notice(cause instanceof Error ? cause.message : String(cause)));
      }
    } else if (entry.size > PEEK_MAX_IMAGE_BYTES) {
      notice(COPY.explorer.peekTooLarge(entry.size));
    } else {
      void fs
        .read(entry.path)
        .then((bytes) => {
          if (cancelled) return;
          // A blob URL, never a data URL: the bytes never become a string, and revoking it on
          // close is what keeps flipping through a folder of photos from leaking every one.
          url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: imageMime(entry.path) }));
          setContent({ state: 'image', url });
        })
        .catch((cause: unknown) => notice(cause instanceof Error ? cause.message : String(cause)));
    }

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fs, entry.path, entry.size]);

  /**
   * §34.6 — the duration, once the element has read the header.
   *
   * `duration` is captured **before** `setContent`, not inside the updater. React nulls a synthetic
   * event's `currentTarget` as soon as the handler returns, and a `useState` updater runs later —
   * so reading it in there threw `Cannot read properties of null`, which is how this was found.
   */
  const onMediaMetadata = (event: React.SyntheticEvent<HTMLMediaElement>): void => {
    const { duration } = event.currentTarget;
    setContent((current) => (current.state === 'media' ? { ...current, duration } : current));
  };

  return (
    <div
      className="explorer-peek"
      role="dialog"
      aria-label={entry.name}
      data-testid="explorer-peek"
      onClick={onClose}
    >
      <div className="explorer-peek-card" onClick={(event) => event.stopPropagation()}>
        <header className="explorer-peek-head">
          <span data-testid="explorer-peek-name">{entry.name}</span>
          <button
            type="button"
            className="explorer-button"
            aria-label={COPY.explorer.peekClose}
            data-testid="explorer-peek-close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="explorer-peek-body">
          {content.state === 'text' ? (
            <pre className="explorer-peek-text" data-testid="explorer-peek-text">
              {content.text}
            </pre>
          ) : null}
          {content.state === 'image' ? (
            <img
              className="explorer-peek-image"
              src={content.url}
              alt={entry.name}
              data-testid="explorer-peek-image"
            />
          ) : null}
          {content.state === 'pdf' ? (
            <div className="explorer-peek-pdf" data-testid="explorer-peek-pdf" data-pages={content.pages}>
              <PdfPageView page={content.page} zoom={PEEK_PDF_ZOOM} />
            </div>
          ) : null}
          {content.state === 'archive' ? (
            <div data-testid="explorer-peek-archive">
              <ul className="explorer-peek-archive">
                {content.names.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              {content.more > 0 ? (
                <p className="explorer-peek-notice">{COPY.archive.peekMore(content.more)}</p>
              ) : null}
            </div>
          ) : null}
          {content.state === 'media' ? (
            <div className="explorer-peek-media" data-testid="explorer-peek-media">
              {content.kind === 'video' ? (
                <video
                  className="explorer-peek-video"
                  data-testid="explorer-peek-media-element"
                  src={content.url}
                  preload="metadata"
                  onLoadedMetadata={onMediaMetadata}
                />
              ) : (
                <audio
                  data-testid="explorer-peek-media-element"
                  src={content.url}
                  preload="metadata"
                  onLoadedMetadata={onMediaMetadata}
                />
              )}
              <p className="explorer-peek-notice" data-testid="explorer-peek-media-meta">
                {COPY.media.peekLine(content.kind, formatTime(content.duration), extname(entry.path))}
              </p>
            </div>
          ) : null}
          {content.state === 'notice' ? (
            <p className="explorer-peek-notice" data-testid="explorer-peek-notice">
              {content.message}
            </p>
          ) : null}
        </div>
        <footer className="explorer-peek-foot" data-testid="explorer-peek-meta">
          {formatSize(entry.size)}
        </footer>
      </div>
    </div>
  );
}
